import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';

// GraphQL query for fetching paginated users
export const GET_ALL_USERS_QUERY = gql`
  query GetAllUsers($where: UserWhereInput, $pagination: UserPaginationArgs) {
    usersPaginated(where: $where, pagination: $pagination) {
      users {
        id
        username
        name
        email
        phone
        role {
          id
          name
        }
        Balance {
          id
          amount
          credit
          onHold
        }
        credit
        balanceIn
        balanceOut
        isActive
        parentId
        comment
        lastLogin
        loginIp
        avatarUrl
        language
        timezone
        createdAt
        updatedAt
        isWagerEnabled
        isCashbackEnabled
        isOnlinePaymentEnabled
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
        total
      }
    }
  }
`;

// Types for the response
export interface Balance {
  id: number;
  amount: number;
  credit: number;
  onHold: number;
}

export interface Role {
  id: number;
  name: string;
}

export interface User {
  id: number;
  username: string;
  email?: string;
  phone?: string;
  role: Role;
  Balance?: Balance;
  credit: number;
  isActive: boolean;
  parentId?: number;
  comment?: string;
  lastLogin?: string;
  balanceOut: number;
  balanceIn: number;
  loginIp?: string;
  avatarUrl?: string;
  language?: string;
  timezone?: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  isWagerEnabled: boolean;
  isCashbackEnabled: boolean;
  isOnlinePaymentEnabled: boolean;
}

export interface PageInfo {
  startCursor?: string;
  endCursor?: string;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  total: number;
}

export interface UsersPaginatedResponse {
  users: User[];
  pageInfo: PageInfo;
}

export interface GetAllUsersResponse {
  usersPaginated: UsersPaginatedResponse;
}

// GetAllUsers service class
export class GetAllUsersService {
  private static instance: GetAllUsersService;

  private constructor() {}

  public static getInstance(): GetAllUsersService {
    if (!GetAllUsersService.instance) {
      GetAllUsersService.instance = new GetAllUsersService();
    }
    return GetAllUsersService.instance;
  }

  // Fetch all users with roleId 3, paginated
  async getAllUsers(
    roleId: number = 3,
    first: number = 10,
  ): Promise<UsersPaginatedResponse> {
    try {
      const response = await apolloClient.query<GetAllUsersResponse>({
        query: GET_ALL_USERS_QUERY,
        variables: {
          where: { roleId },
          pagination: { first },
        },
        fetchPolicy: 'network-only',
      });
      if (response.errors && response.errors.length > 0) {
        throw response;
      }
      if (!response.data || !response.data.usersPaginated) {
        throw new Error('No data returned from getAllUsers query');
      }
      return response.data.usersPaginated;
    } catch (error: any) {
      console.error('Error fetching users:', error);
      // Log more detailed error information
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }

  // Fetch users with custom filters
  async getUsersWithFilters(
    where: any,
    pagination: any,
  ): Promise<UsersPaginatedResponse> {
    try {
      const response = await apolloClient.query<GetAllUsersResponse>({
        query: GET_ALL_USERS_QUERY,
        variables: {
          where,
          pagination,
        },
        fetchPolicy: 'network-only',
      });
      if (response.errors && response.errors.length > 0) {
        throw response;
      }
      if (!response.data || !response.data.usersPaginated) {
        throw new Error('No data returned from getUsersWithFilters query');
      }
      return response.data.usersPaginated;
    } catch (error: any) {
      console.error('Error fetching users with filters:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const getAllUsersService = GetAllUsersService.getInstance();

// Export convenience function for backward compatibility
export async function getAllUsers(
  roleId: number = 3,
  first: number = 10,
): Promise<UsersPaginatedResponse> {
  return getAllUsersService.getAllUsers(roleId, first);
}
